import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { SYMBOL_GROUPS, type LatexSymbol } from "../editor/symbols";
import { Popover } from "./Popover";

/** A searchable grid of symbols. Picking one calls onPick with its LaTeX; insert it with insertSymbol(view, latex, math). */
export function SymbolPicker({
  anchor,
  onPick,
  onClose,
}: {
  anchor: HTMLElement;
  onPick: (latex: string, math: boolean) => void;
  onClose: () => void;
}) {
  const [group, setGroup] = useState(SYMBOL_GROUPS[0].name);
  const [query, setQuery] = useState("");
  const [hover, setHover] = useState<LatexSymbol | null>(null);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^\\/, "");
    if (!q) return SYMBOL_GROUPS.find((g) => g.name === group)!.symbols;
    return SYMBOL_GROUPS.flatMap((g) => g.symbols).filter((s) => s.name.toLowerCase().includes(q) || s.latex.toLowerCase().includes(q) || s.char === q);
  }, [group, query]);

  const pick = (s: LatexSymbol) => {
    onPick(s.latex, s.math);
    onClose();
  };

  return (
    <Popover anchor={anchor} onClose={onClose} className="symbol-picker" label="Insert symbol">
      <div className="symbol-search">
        <Search size={14} />
        <input
          autoFocus
          placeholder="Search symbols"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && shown[0]) pick(shown[0]);
          }}
        />
      </div>
      {!query.trim() && (
        <div className="symbol-groups" role="tablist">
          {SYMBOL_GROUPS.map((g) => (
            <button key={g.name} role="tab" aria-selected={g.name === group} className={g.name === group ? "on" : ""} onClick={() => setGroup(g.name)}>
              {g.name}
            </button>
          ))}
        </div>
      )}
      <div className="symbol-grid">
        {shown.map((s) => (
          <button
            key={s.latex + s.name}
            className={`symbol${s.math ? "" : " text"}`}
            title={`${s.name}: ${s.latex}`}
            onMouseEnter={() => setHover(s)}
            onFocus={() => setHover(s)}
            onClick={() => pick(s)}
          >
            {s.char}
          </button>
        ))}
        {!shown.length && <div className="symbol-none">No symbols match “{query}”</div>}
      </div>
      <div className="symbol-foot">
        {hover ? (
          <>
            <span>{hover.name}</span>
            <code>{hover.latex}</code>
          </>
        ) : (
          <span>Maths symbols get wrapped in \( \) when the cursor isn't already in maths</span>
        )}
      </div>
    </Popover>
  );
}
